/**
 * Store: Index
 * -----------------------------------------------------------------------------
 * Root store for all theme level states.
 * - Tied to the index component.
 * - Only JSDoc custom parameters, ignore module state or object.
 *
 */
import { defineStore } from 'pinia';
import useCartStore from '~stores/cart';

const useIndexStore = defineStore('index', {
  state: () => ({
    hydrated: false,
    notification: false,
    previousUrl: '/',
    geolocationCountry: {
      code: false,
      name: false,
    },
    geolocationLanguage: false,
    screen: {
      breakpoint: 'xs',
      height: 0,
      mobile: true,
      width: 0,
    },
    scroll: {
      direction: 'none',
      header: 'open',
      paused: false,
      top: true,
      x: 0,
      y: 0,
    },
    settings: {
      mobileBreakpoints: ['xs', 's', 'm'],
      rtlLanguages: [
        'ar',
        'dv',
        'fa',
        'ff',
        'he',
        'ks',
        'ku',
        'ur',
        'wo',
        'yi',
      ],
      start: 100,
      threshold: 5,
      throttle: 250,
    },
    stylesheets: [],
  }),

  getters: {
    /**
     * Get if stylesheet is in stylesheets.
     * - Does not need to be complete match to be true.
     * @param {String} stylesheet - Stylesheet to test.
     * @returns {Boolean}
     */
    getLoadedStylesheet: (state) => (stylesheet) => {
      return state.stylesheets.some((loadedStylesheet) => {
        return loadedStylesheet.includes(stylesheet);
      });
    },

    /**
     * Get if RTL writing direction.
     * @returns {Boolean}
     */
    getRtlWritingDirection(state) {
      return state.settings.rtlLanguages.includes(cnvs.store.language);
    },

    getGeolocationCountry(state) {
      return state.geolocationCountry;
    },

    getGeolocationLanguage(state) {
      return state.geolocationLanguage;
    },
  },

  actions: {
    /**
     * Commit rehydrated state.
     * - Runs initial items.
     */
    async rehydrateSite() {
      this.hydrated = true;
      const cartStore = useCartStore();

      await Promise.all([cartStore.setResponse(true)]);
    },

    /**
     * Commit mobile breakpoints state.
     * @param {Array} mobileBreakpoints - Breakpoints considered as mobile.
     */
    setMobileBreakpoints(mobileBreakpoints) {
      this.settings.mobileBreakpoints = mobileBreakpoints;
    },

    /**
     * Commit notification state.
     * @param {Boolean|Object} notification - Notification to display, see
     * notification-panel.vue for props, accepts false to hide.
     */
    setNotification(notification = {}) {
      this.notification = notification;
    },

    /**
     * Commit previous URL state.
     * @param {String} pathname - Previous pathname.
     */
    setPreviousUrl(pathname) {
      this.previousUrl = pathname;
    },

    /**
     * Commit screen state.
     * @param {Object} screen - Screen state object.
     */
    setScreen(screen) {
      this.screen = screen;
    },

    /**
     * Set scroll state.
     * - Does not update paused state.
     * @param {Object} scroll - Scroll state object.
     */
    setScroll(scroll) {
      this.scroll = {
        ...scroll,
        paused: this.scroll.paused,
      };
    },

    /**
     * Set scroll start.
     * @param {String} start - New scroll start.
     */
    setScrollStart(start) {
      this.settings.start = start;
    },

    /**
     * Commit loaded stylesheet state.
     * @param {String} stylesheet - Loaded stylesheet file name.
     */
    setStylesheet(stylesheet) {
      this.stylesheets = [...this.stylesheets, stylesheet];
    },

    /**
     *
     * @param {String} country - country handle as returned by
     * https://shopify.dev/docs/storefronts/themes/markets/localization-discovery
     */
    setGeolocationCountry(country) {
      this.geolocationCountry = country;
    },

    /**
     *
     * @param {String} lang - lang handle as returned by
     * https://shopify.dev/docs/storefronts/themes/markets/localization-discovery
     */
    setGeolocationLanguage(lang) {
      this.geolocationLanguage = lang;
    },
  },
  persist: {
    pick: ['notifications', 'geolocationCountry', 'geolocationLanguage'],
  },
});

export default useIndexStore;
